import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, Maximize2, Minimize2 } from 'lucide-react';
import { ProjectStatusChart} from './Charts';
import { KPICard } from './KPICard';
import { Briefcase, CheckSquare, AlertCircle, Clock3 } from 'lucide-react';

const ProjectDashboardModal = ({ project, ship, yard, onClose, isFullView, toggleFullView }) => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  // Handle window resize for responsive adjustments
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate chart height based on available space
  const calculateChartHeight = () => {
    const isMobile = windowSize.width < 768;
    const headerHeight = 64; // Header height in pixels
    const footerHeight = 0;  // No explicit footer
    const kpiCardsHeight = isMobile ? 400 : 160; // KPI cards take more vertical space on mobile
    const padding = 48; // Padding total (top + bottom)
    const gap = 48; // Total gap space between elements
    
    let availableHeight;
    if (isFullView) {
      availableHeight = windowSize.height - headerHeight - kpiCardsHeight - padding - gap;
    } else {
      availableHeight = Math.min(windowSize.height * 0.7, 600) - headerHeight - kpiCardsHeight - padding - gap;
    }
    
    // Calculate chart height based on layout
    const numRows = isMobile ? 3 : 2; // On mobile, we stack all charts
    return Math.max(200, (availableHeight / numRows) - 20); // Minimum height of 200px per chart
  };

  if (!project || !ship || !yard) return null;

  const isMobile = windowSize.width < 768;
  const isTablet = windowSize.width >= 768 && windowSize.width < 1024;
  const chartHeight = calculateChartHeight();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div 
        className={`bg-white rounded-lg shadow-lg flex flex-col ${
          isFullView ? 'w-full h-full' : 'w-full max-w-4xl max-h-[90vh]'
        } overflow-hidden`}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-3 sm:p-4 border-b shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-4 overflow-hidden">
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 flex items-center flex-shrink-0"
            >
              <ChevronLeft size={isMobile ? 20 : 24} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <h2 className="text-base sm:text-xl font-bold text-gray-800 truncate">
              {ship.name} - {yard.yardNo}
            </h2>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            <button 
              onClick={toggleFullView} 
              className="text-gray-500 hover:text-gray-700"
            >
              {isFullView ? <Minimize2 size={isMobile ? 20 : 24} /> : <Maximize2 size={isMobile ? 20 : 24} />}
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={isMobile ? 20 : 24} />
            </button>
          </div>
        </div>

        {/* Modal Content - Using flex-grow to take available space without scrolling */}
        <div className="p-3 sm:p-6 overflow-y-auto flex-grow">
          <div className="h-full flex flex-col" style={{ minHeight: '100%' }}>
            {/* KPI Cards - Grid layout that adapts to screen size */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <KPICard 
                icon={<Briefcase size={isMobile ? 32 : 40} className="text-blue-600" />}
                title="Progress" 
                value={`${yard.progress}%`} 
                trend="On Track"
                bgColor="bg-blue-200"
              />
              <KPICard 
                icon={<CheckSquare size={isMobile ? 32 : 40} className="text-green-600" />}
                title="Completed Tasks" 
                value={`${ship.tasks?.completed || 0}/${ship.tasks?.total || 0}`} 
                trend="+12% from last week"
                bgColor="bg-green-200"
              />
              <KPICard 
                icon={<AlertCircle size={isMobile ? 32 : 40} className="text-yellow-600" />}
                title="Remaining Tasks" 
                value={`${(ship.tasks?.total || 0) - (ship.tasks?.completed || 0)}`} 
                trend="+2 from last week"
                trendUp={false}
                bgColor="bg-yellow-200"
              />
              <KPICard 
                icon={<Clock3 size={isMobile ? 32 : 40} className="text-red-600" />}
                title="Days Left" 
                value={Math.ceil((new Date(project.dueDate) - new Date()) / (1000 * 60 * 60 * 24))} 
                trend="On Track"
                bgColor="bg-red-200"
              />
            </div>

            {/* Charts - Flexible layout that adjusts based on screen size */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6 flex-grow">
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col h-full">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-4">Project Status</h3>
                <div className="flex-grow" style={{ height: chartHeight }}>
                  <ProjectStatusChart />
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col h-full">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-4">Progress Over Time</h3>
                {/* <div className="flex-grow" style={{ height: chartHeight }}>
                  <ProgressOverTimeChart />
                </div> */}
              </div>
            </div>

            {/* Team Performance */}
            <div className="bg-white rounded-lg shadow p-3 sm:p-4 flex flex-col mb-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-4">Team Performance</h3>
              {/* <div style={{ height: chartHeight }}>
                <TeamPerformanceChart />
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboardModal;